/**
 * Central token storage for development Keycloak IAM.
 * Remember Me stores username only. Stay Signed In controls persistent token storage.
 */

import { decodeJwtPayload, type DecodedJwtProfile } from './jwtPayload';
import { isCookieAuthMode } from './cookieAuthMode';
import {
  broadcastAuthLogout,
  broadcastAuthTokenUpdated,
} from './authSync';

const ACCESS_TOKEN_KEY = 'march_dev_keycloak_access_token';
const REFRESH_TOKEN_KEY = 'march_dev_keycloak_refresh_token';
const EXPIRES_AT_KEY = 'march_dev_keycloak_expires_at';
const REFRESH_EXPIRES_AT_KEY = 'march_dev_keycloak_refresh_expires_at';
const ID_TOKEN_KEY = 'march_dev_keycloak_id_token';
const TOKEN_TYPE_KEY = 'march_dev_keycloak_token_type';
const CLAIMS_KEY = 'march_dev_keycloak_claims_json';
const CREATED_AT_KEY = 'march_dev_keycloak_created_at';
const UPDATED_AT_KEY = 'march_dev_keycloak_updated_at';
const PKCE_VERIFIER_KEY = 'march_oidc_pkce_verifier';
const OAUTH_STATE_KEY = 'march_oidc_state';
const STORAGE_SCOPE_KEY = 'march_auth_storage_scope';
const REMEMBERED_USERNAME_KEY = 'march_remembered_username';
const STAY_SIGNED_IN_PREF_KEY = 'march_stay_signed_in_pref';

/** Legacy keys from pre-renaming local IAM mode — read once then migrate. */
const LEGACY_ACCESS_TOKEN_KEY = 'march_local_keycloak_access_token';
const LEGACY_REFRESH_TOKEN_KEY = 'march_local_keycloak_refresh_token';
const LEGACY_EXPIRES_AT_KEY = 'march_local_keycloak_expires_at';
const LEGACY_CLAIMS_KEY = 'march_local_keycloak_claims_json';

const TOKEN_KEYS = [
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  EXPIRES_AT_KEY,
  REFRESH_EXPIRES_AT_KEY,
  ID_TOKEN_KEY,
  TOKEN_TYPE_KEY,
  CLAIMS_KEY,
  CREATED_AT_KEY,
  UPDATED_AT_KEY,
];

export type DevAuthSession = {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
  refreshExpiresAt?: number;
  idToken?: string;
  tokenType?: string;
  claims?: DecodedJwtProfile | null;
  staySignedIn?: boolean;
  createdAt?: number;
  updatedAt?: number;
};

/** @deprecated use {@link DevAuthSession} */
export type LocalAuthSession = DevAuthSession;

export type TokenState = {
  access_token?: string;
  refresh_token?: string;
  id_token?: string;
  token_type?: string;
  expires_at?: number;
  refresh_expires_at?: number;
  remembered_username?: string;
  stay_signed_in?: boolean;
  created_at?: number;
  updated_at?: number;
};

export type LoginSessionOptions = {
  rememberUsername?: boolean;
  staySignedIn?: boolean;
  username?: string;
};

let memoryAccessToken: string | null = null;

function defaultStaySignedIn(): boolean {
  // SEC-03a (fail-safe token storage): tokens default to tab-scoped
  // sessionStorage — cleared when the tab closes, invisible to other tabs'
  // localStorage — unless the user explicitly opts into "stay signed in"
  // (or a deployment sets VITE_AUTH_DEFAULT_STAY_SIGNED_IN=true).
  const raw = (import.meta.env.VITE_AUTH_DEFAULT_STAY_SIGNED_IN ?? 'false')
    .toString()
    .trim()
    .toLowerCase();
  return ['1', 'true', 'yes', 'on'].includes(raw);
}

function sessionStorageSafe(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function localStorageSafe(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function migrateLegacyItem(store: Storage, primary: string, legacy: string): void {
  const legacyValue = store.getItem(legacy);
  if (legacyValue && !store.getItem(primary)) {
    store.setItem(primary, legacyValue);
  }
  store.removeItem(legacy);
}

function migrateLegacyKeys(store: Storage): void {
  migrateLegacyItem(store, ACCESS_TOKEN_KEY, LEGACY_ACCESS_TOKEN_KEY);
  migrateLegacyItem(store, REFRESH_TOKEN_KEY, LEGACY_REFRESH_TOKEN_KEY);
  migrateLegacyItem(store, EXPIRES_AT_KEY, LEGACY_EXPIRES_AT_KEY);
  migrateLegacyItem(store, CLAIMS_KEY, LEGACY_CLAIMS_KEY);
}

function readStorageScope(): 'local' | 'session' {
  const local = localStorageSafe();
  if (!local) return 'session';
  const scope = (local.getItem(STORAGE_SCOPE_KEY) ?? '').trim();
  if (scope === 'local') return 'local';
  if (scope === 'session') return 'session';
  const sessionStore = sessionStorageSafe();
  if (sessionStore?.getItem(ACCESS_TOKEN_KEY)) return 'session';
  if (local.getItem(ACCESS_TOKEN_KEY)) return 'local';
  return 'session';
}

function writeStorageScope(scope: 'local' | 'session'): void {
  const local = localStorageSafe();
  if (!local) return;
  local.setItem(STORAGE_SCOPE_KEY, scope);
}

function tokenStorage(): Storage | null {
  const scope = readStorageScope();
  return scope === 'local' ? localStorageSafe() : sessionStorageSafe();
}

/** Storage bucket used for auth tokens (local when stay-signed-in, else session). */
export function getAuthScopedStorage(): Storage | null {
  return tokenStorage();
}

function clearTokenKeysFrom(store: Storage | null): void {
  if (!store) return;
  for (const key of TOKEN_KEYS) {
    store.removeItem(key);
  }
  migrateLegacyKeys(store);
  for (const key of [
    LEGACY_ACCESS_TOKEN_KEY,
    LEGACY_REFRESH_TOKEN_KEY,
    LEGACY_EXPIRES_AT_KEY,
    LEGACY_CLAIMS_KEY,
  ]) {
    store.removeItem(key);
  }
}

function clearAllTokenStorage(): void {
  clearTokenKeysFrom(sessionStorageSafe());
  clearTokenKeysFrom(localStorageSafe());
  // SEC-I5: the cookie-mode marker is session state too. Leaving it behind on
  // logout would render a signed-in shell with no session behind it.
  clearCookieSessionMarker();
  memoryAccessToken = null;
}

function syncMemoryFromStore(): void {
  const store = tokenStorage();
  if (!store) {
    memoryAccessToken = null;
    return;
  }
  migrateLegacyKeys(store);
  memoryAccessToken = (store.getItem(ACCESS_TOKEN_KEY) ?? '').trim() || null;
}

export function getStaySignedIn(): boolean {
  const local = localStorageSafe();
  if (!local) return defaultStaySignedIn();
  const stored = local.getItem(STAY_SIGNED_IN_PREF_KEY);
  if (stored === '1') return true;
  if (stored === '0') return false;
  return defaultStaySignedIn();
}

export function setStaySignedIn(value: boolean): void {
  const local = localStorageSafe();
  if (!local) return;
  if (value) {
    local.setItem(STAY_SIGNED_IN_PREF_KEY, '1');
  } else {
    local.setItem(STAY_SIGNED_IN_PREF_KEY, '0');
  }
}

export function getRememberedUsername(): string {
  const local = localStorageSafe();
  if (!local) return '';
  return (local.getItem(REMEMBERED_USERNAME_KEY) ?? '').trim();
}

export function setRememberedUsername(username: string): void {
  const local = localStorageSafe();
  if (!local) return;
  const trimmed = username.trim();
  if (trimmed) {
    local.setItem(REMEMBERED_USERNAME_KEY, trimmed);
  } else {
    local.removeItem(REMEMBERED_USERNAME_KEY);
  }
}

export function applyLoginSessionOptions(options: LoginSessionOptions): void {
  if (options.rememberUsername && options.username?.trim()) {
    setRememberedUsername(options.username);
  } else if (!options.rememberUsername) {
    setRememberedUsername('');
  }
  setStaySignedIn(Boolean(options.staySignedIn));
}

function isExpired(expiresAtMs: number | null | undefined): boolean {
  if (!expiresAtMs) return false;
  return Date.now() >= expiresAtMs;
}

export function isAccessTokenExpired(): boolean {
  return isExpired(getAccessTokenExpiresAtMs());
}

export function isAccessTokenExpiringSoon(thresholdMs = 60_000): boolean {
  const expiresAt = getAccessTokenExpiresAtMs();
  if (!expiresAt) return false;
  return Date.now() >= expiresAt - thresholdMs;
}

export function isRefreshTokenExpired(): boolean {
  return isExpired(getRefreshExpiresAtMs());
}

/** Raw access token without clearing on expiry (for refresh orchestration). */
export function getStoredAccessToken(): string {
  if (memoryAccessToken) return memoryAccessToken;
  const store = tokenStorage();
  if (!store) return '';
  migrateLegacyKeys(store);
  return (store.getItem(ACCESS_TOKEN_KEY) ?? '').trim();
}

export function getAccessToken(): string {
  return getStoredAccessToken();
}

export function getAccessTokenExpiresAtMs(): number | null {
  const store = tokenStorage();
  if (!store) return null;
  migrateLegacyKeys(store);
  const raw = store.getItem(EXPIRES_AT_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getRefreshExpiresAtMs(): number | null {
  const store = tokenStorage();
  if (!store) return null;
  migrateLegacyKeys(store);
  const raw = store.getItem(REFRESH_EXPIRES_AT_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getStoredIdToken(): string {
  const store = tokenStorage();
  if (!store) return '';
  migrateLegacyKeys(store);
  return (store.getItem(ID_TOKEN_KEY) ?? '').trim();
}

export function getDevAccessToken(): string {
  const token = getStoredAccessToken();
  if (!token) return '';
  if (isAccessTokenExpired()) return '';
  return token;
}

/** @deprecated use {@link getDevAccessToken} */
export function getLocalAccessToken(): string {
  return getDevAccessToken();
}

export function getRefreshToken(): string {
  return getDevRefreshToken();
}

export function getDevRefreshToken(): string {
  const store = tokenStorage();
  if (!store) return '';
  migrateLegacyKeys(store);
  return (store.getItem(REFRESH_TOKEN_KEY) ?? '').trim();
}

/** @deprecated use {@link getDevRefreshToken} */
export function getLocalRefreshToken(): string {
  return getDevRefreshToken();
}

export function getStoredClaims(): DecodedJwtProfile | null {
  const store = tokenStorage();
  if (!store) return null;
  migrateLegacyKeys(store);
  const raw = store.getItem(CLAIMS_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as DecodedJwtProfile;
  } catch {
    return null;
  }
}

export function getTokenState(): TokenState {
  const store = tokenStorage();
  const createdRaw = store?.getItem(CREATED_AT_KEY);
  const updatedRaw = store?.getItem(UPDATED_AT_KEY);
  return {
    access_token: getStoredAccessToken() || undefined,
    refresh_token: getDevRefreshToken() || undefined,
    id_token: getStoredIdToken() || undefined,
    token_type: store?.getItem(TOKEN_TYPE_KEY) ?? undefined,
    expires_at: getAccessTokenExpiresAtMs() ?? undefined,
    refresh_expires_at: getRefreshExpiresAtMs() ?? undefined,
    remembered_username: getRememberedUsername() || undefined,
    stay_signed_in: readStorageScope() === 'local',
    created_at: createdRaw ? Number(createdRaw) : undefined,
    updated_at: updatedRaw ? Number(updatedRaw) : undefined,
  };
}

export function setDevAccessToken(token: string): void {
  setDevAuthSession({ accessToken: token });
}

/** @deprecated use {@link setDevAccessToken} */
export function setLocalAccessToken(token: string): void {
  setDevAccessToken(token);
}

export function setTokenState(
  state: TokenState,
  options?: LoginSessionOptions,
): void {
  const accessToken = (state.access_token ?? '').trim();
  if (!accessToken) return;
  setDevAuthSession({
    accessToken,
    refreshToken: state.refresh_token,
    expiresAt: state.expires_at,
    refreshExpiresAt: state.refresh_expires_at,
    idToken: state.id_token,
    tokenType: state.token_type,
    staySignedIn: state.stay_signed_in,
    createdAt: state.created_at,
    updatedAt: state.updated_at,
  }, options);
}

export function setDevAuthSession(
  sessionData: DevAuthSession,
  options?: LoginSessionOptions,
): void {
  if (options) applyLoginSessionOptions(options);

  const staySignedIn = sessionData.staySignedIn ?? options?.staySignedIn ?? getStaySignedIn();
  const scope: 'local' | 'session' = staySignedIn ? 'local' : 'session';
  writeStorageScope(scope);

  const targetStore = scope === 'local' ? localStorageSafe() : sessionStorageSafe();
  const otherStore = scope === 'local' ? sessionStorageSafe() : localStorageSafe();
  clearTokenKeysFrom(otherStore);

  if (!targetStore) return;
  migrateLegacyKeys(targetStore);

  const now = Date.now();
  const existingCreated = Number(targetStore.getItem(CREATED_AT_KEY));
  const createdAt =
    sessionData.createdAt ?? (Number.isFinite(existingCreated) ? existingCreated : now);
  const updatedAt = sessionData.updatedAt ?? now;

  targetStore.setItem(ACCESS_TOKEN_KEY, sessionData.accessToken.trim());
  memoryAccessToken = sessionData.accessToken.trim();

  if (sessionData.refreshToken) {
    targetStore.setItem(REFRESH_TOKEN_KEY, sessionData.refreshToken.trim());
  } else {
    targetStore.removeItem(REFRESH_TOKEN_KEY);
  }
  if (sessionData.expiresAt) {
    targetStore.setItem(EXPIRES_AT_KEY, String(sessionData.expiresAt));
  } else {
    targetStore.removeItem(EXPIRES_AT_KEY);
  }
  if (sessionData.refreshExpiresAt) {
    targetStore.setItem(REFRESH_EXPIRES_AT_KEY, String(sessionData.refreshExpiresAt));
  } else {
    targetStore.removeItem(REFRESH_EXPIRES_AT_KEY);
  }
  if (sessionData.idToken) {
    targetStore.setItem(ID_TOKEN_KEY, sessionData.idToken.trim());
  } else {
    targetStore.removeItem(ID_TOKEN_KEY);
  }
  if (sessionData.tokenType) {
    targetStore.setItem(TOKEN_TYPE_KEY, sessionData.tokenType.trim());
  } else {
    targetStore.removeItem(TOKEN_TYPE_KEY);
  }
  const claims = sessionData.claims ?? decodeJwtPayload(sessionData.accessToken);
  if (claims) {
    targetStore.setItem(CLAIMS_KEY, JSON.stringify(claims));
  } else {
    targetStore.removeItem(CLAIMS_KEY);
  }
  targetStore.setItem(CREATED_AT_KEY, String(createdAt));
  targetStore.setItem(UPDATED_AT_KEY, String(updatedAt));

  broadcastAuthTokenUpdated();
}

/** @deprecated use {@link setDevAuthSession} */
export function setLocalAuthSession(sessionData: DevAuthSession): void {
  setDevAuthSession(sessionData);
}

/**
 * SEC-I5 — "there is a signed-in session" marker for cookie mode.
 *
 * In cookie mode the credential is httpOnly, so this code CANNOT see it, and
 * `hasDevAccessToken()` — the single gate the whole app asks "am I signed in?"
 * — would answer no forever. This marker answers that question instead.
 *
 * It holds NO credential and grants NO access: forging it in devtools gets you
 * a UI that renders and then 401s on the first request, because the server only
 * ever trusts the cookie. It is a rendering hint, which is all the old token
 * check ever was for this purpose.
 *
 * Session-scoped deliberately. "Stay signed in" is the cookie's `Max-Age` to
 * decide now; a marker outliving the cookie would show a signed-in shell to
 * someone whose session is gone.
 */
const COOKIE_SESSION_KEY = 'march_cookie_session';

export function markCookieSessionActive(): void {
  sessionStorageSafe()?.setItem(COOKIE_SESSION_KEY, '1');
}

export function clearCookieSessionMarker(): void {
  sessionStorageSafe()?.removeItem(COOKIE_SESSION_KEY);
  localStorageSafe()?.removeItem(COOKIE_SESSION_KEY);
}

export function hasCookieSessionMarker(): boolean {
  return sessionStorageSafe()?.getItem(COOKIE_SESSION_KEY) === '1';
}

export function hasDevAccessToken(): boolean {
  if (isCookieAuthMode()) return hasCookieSessionMarker();
  const token = getStoredAccessToken();
  if (!token) return false;
  if (isAccessTokenExpired()) {
    return getDevRefreshToken().length > 0 && !isRefreshTokenExpired();
  }
  return true;
}

/** @deprecated use {@link hasDevAccessToken} */
export function hasLocalAccessToken(): boolean {
  return hasDevAccessToken();
}

export function reloadTokenStateFromStorage(): void {
  syncMemoryFromStore();
}

export function setPkceVerifier(verifier: string): void {
  const store = sessionStorageSafe();
  if (!store) return;
  store.setItem(PKCE_VERIFIER_KEY, verifier);
}

export function getPkceVerifier(): string {
  const store = sessionStorageSafe();
  if (!store) return '';
  return (store.getItem(PKCE_VERIFIER_KEY) ?? '').trim();
}

export function setOAuthState(state: string): void {
  const store = sessionStorageSafe();
  if (!store) return;
  store.setItem(OAUTH_STATE_KEY, state);
}

export function getOAuthState(): string {
  const store = sessionStorageSafe();
  if (!store) return '';
  return (store.getItem(OAUTH_STATE_KEY) ?? '').trim();
}

export function clearTokenState(options?: { broadcast?: boolean }): void {
  clearAllTokenStorage();
  writeStorageScope('session');
  const local = localStorageSafe();
  if (local) local.removeItem(STORAGE_SCOPE_KEY);
  if (options?.broadcast) {
    broadcastAuthLogout();
  }
}

export function clearDevAuthSession(): void {
  clearTokenState();
}

/** @deprecated use {@link clearDevAuthSession} */
export function clearLocalAuthSession(): void {
  clearDevAuthSession();
}
